import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import {
  DEFAULT_TOAST_DURATION_MS,
  ToastMessage,
  ToastService,
} from '@aurrica-web/core-data';
import {
  TOAST_DECOR_BLOB,
  TOAST_DECOR_CIRCLE_LG,
  TOAST_DECOR_CIRCLE_MD,
  TOAST_DECOR_CIRCLE_SM,
  TOAST_SPEECH_BUBBLE,
  TOAST_THEMES,
  resolveToastType,
  toastIconPath,
} from './toast-art';

/**
 * Fixed stack of transient notifications driven by {@link ToastService}.
 *
 * @remarks
 * - Mount **once** at app shell level (e.g. next to `router-outlet`).
 * - Call `ToastService.show({ message, type?, detail?, duration? })` from
 *   anywhere `ToastService` is injectable.
 * - **`duration`** defaults to {@link DEFAULT_TOAST_DURATION_MS}; omit for the
 *   standard ~3s auto-dismiss.
 * - Dismissal clears the auto-dismiss timer so no orphaned timeouts run after
 *   manual close.
 * - Exit animation timing is derived from each toast’s `duration` so the CSS
 *   fade can finish before removal when auto-dismiss fires.
 *
 * @example
 * ```html
 * <lib-ui-toast />
 * ```
 *
 * ```ts
 * readonly toast = inject(ToastService);
 * this.toast.show({ message: 'Saved', type: 'success' });
 * ```
 */
@Component({
  selector: 'lib-ui-toast',
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
  <!-- pt-7 reserves space so the half-overlapping bubble is not clipped by the viewport -->
    <div
      class="pointer-events-none fixed top-6 right-6 z-100010 flex w-[min(20rem,calc(100vw-2rem))] flex-col gap-4 pt-7"
      role="region"
      aria-label="Notifications"
    >
      @for (toast of toasts(); track toast.id) {
        <div
          class="toast-card pointer-events-auto group relative overflow-visible"
          [ngStyle]="cardStyle(toast)"
          [attr.role]="liveRole(toast.type)"
          aria-atomic="true"
        >
          <!-- Speech bubble: ~half the circle sits above the toast top edge -->
          <div
            class="pointer-events-none absolute left-3.5 top-0 z-20 -translate-y-1/2"
            aria-hidden="true"
          >
            <svg
              class="block h-14 w-12 drop-shadow-sm"
              viewBox="0 0 70 80"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                [attr.d]="speechBubble"
                [attr.fill]="theme(toast.type).accent"
              />
              @if (resolveType(toast.type) === 'error') {
                <path
                  d="M31 27.5 39 36.5 M39 27.5 31 36.5"
                  stroke="white"
                  stroke-width="2.75"
                  stroke-linecap="round"
                />
              } @else {
                <path
                  [attr.d]="filledIconPath(toast.type)"
                  fill="white"
                />
              }
            </svg>
          </div>

          <!-- Toast body (background); top padding clears the bubble tail -->
          <div
            class="relative overflow-hidden rounded-2xl shadow-md pt-6 pb-3 pl-[4.25rem] pr-9 min-h-[4.25rem]"
            [ngStyle]="bodyStyle(toast)"
          >
            <div
              class="pointer-events-none absolute inset-y-0 left-0 w-16 overflow-hidden"
              aria-hidden="true"
            >
              <svg
                class="absolute -bottom-2 -left-5 h-[4.25rem] w-[4.25rem]"
                viewBox="0 0 88 88"
                fill="none"
              >
                <circle
                  [attr.cx]="decorLg.cx"
                  [attr.cy]="decorLg.cy"
                  [attr.r]="decorLg.r"
                  [attr.fill]="theme(toast.type).accent"
                />
                <circle
                  [attr.cx]="decorMd.cx"
                  [attr.cy]="decorMd.cy"
                  [attr.r]="decorMd.r"
                  [attr.fill]="theme(toast.type).accent"
                />
                <circle
                  [attr.cx]="decorSm.cx"
                  [attr.cy]="decorSm.cy"
                  [attr.r]="decorSm.r"
                  [attr.fill]="theme(toast.type).accent"
                />
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  [attr.d]="decorBlob"
                  [attr.fill]="theme(toast.type).accent"
                />
              </svg>
            </div>

            <p class="text-sm font-semibold leading-snug text-white">
              {{ toast.message }}
            </p>
            @if (toast.detail) {
              <p class="mt-0.5 text-xs leading-snug text-white/90">
                {{ toast.detail }}
              </p>
            }
          </div>

          <button
            type="button"
            (click)="dismiss(toast.id)"
            class="absolute top-7 right-2.5 z-30 flex h-6 w-6 items-center justify-center rounded-full text-white/90 transition-colors hover:bg-white/15 hover:text-white cursor-pointer"
            aria-label="Dismiss notification"
          >
            <svg
              class="h-3.5 w-3.5"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M3.06 3.06a1 1 0 0 1 1.42 0L8 6.59l3.52-3.53a1 1 0 1 1 1.42 1.42L9.41 8l3.53 3.52a1 1 0 0 1-1.42 1.42L8 9.41l-3.53 3.53a1 1 0 0 1-1.42-1.42L6.59 8 3.06 4.48a1 1 0 0 1 0-1.42Z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>
      }
    </div>
  `,
  styles: [
    `
      .toast-card {
        animation:
          toast-in 0.3s ease-out,
          toast-out 0.4s ease-in forwards;
        animation-delay: 0s, var(--out-delay);
      }

      @keyframes toast-in {
        from {
          transform: translateX(120%) scale(0.95);
          opacity: 0;
        }
        to {
          transform: translateX(0) scale(1);
          opacity: 1;
        }
      }

      @keyframes toast-out {
        to {
          transform: translateY(-10px);
          opacity: 0;
        }
      }
    `,
  ],
})
export class Toast {
  private readonly toastService = inject(ToastService);

  protected readonly speechBubble = TOAST_SPEECH_BUBBLE;
  protected readonly decorBlob = TOAST_DECOR_BLOB;
  protected readonly decorLg = TOAST_DECOR_CIRCLE_LG;
  protected readonly decorMd = TOAST_DECOR_CIRCLE_MD;
  protected readonly decorSm = TOAST_DECOR_CIRCLE_SM;
  protected readonly resolveType = resolveToastType;

  /** Filled SVG path for non-error toast types (error uses stroked lines). */
  protected filledIconPath(
    type: ToastMessage['type'] | undefined,
  ): string {
    const resolved = resolveToastType(type);
    if (resolved === 'error') {
      return '';
    }
    return toastIconPath(resolved);
  }

  /** Snapshot of active toasts from {@link ToastService}. */
  readonly toasts = computed(() => this.toastService.toasts());

  dismiss(id: string): void {
    this.toastService.dismiss(id);
  }

  /** Delay before the exit keyframe runs; keeps exit aligned with auto-dismiss. */
  protected outDelayCss(toast: ToastMessage): string {
    const ms = toast.duration ?? DEFAULT_TOAST_DURATION_MS;
    return `${Math.max(0, ms - 400)}ms`;
  }

  /** Errors use `alert` (assertive); other types use `status` (polite). */
  protected liveRole(
    type: ToastMessage['type'] | undefined,
  ): 'alert' | 'status' {
    return type === 'error' ? 'alert' : 'status';
  }

  protected theme(type: ToastMessage['type'] | undefined) {
    return TOAST_THEMES[resolveToastType(type)];
  }

  /** Exit animation delay on the card wrapper (CSS vars do not bubble up from children). */
  protected cardStyle(toast: ToastMessage): Record<string, string> {
    return {
      '--out-delay': this.outDelayCss(toast),
    };
  }

  protected bodyStyle(toast: ToastMessage): Record<string, string> {
    return {
      backgroundColor: this.theme(toast.type).bg,
    };
  }
}
